/**
 * Provenance screen (WO-25).
 *
 * Reachable at: /(app)/provenance/<factId>
 *
 * The factId is an opaque UUID path param — no PII is in the URL (mobile
 * CLAUDE.md invariant 2). Navigation to this screen passes the factId as a
 * route param, not as a query string with identifying information.
 *
 * Example push: router.push(`/(app)/provenance/${fact.id}`)
 * — called from the review screen's CommittedFactsPanel (low-risk affordance).
 *
 * READ-ONLY: the ProvenanceViewer never mutates state.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Screen, Text, Button } from '../../../src/components/ui';
import { ProvenanceViewer } from '../../../src/components/provenance/ProvenanceViewer';
import { theme } from '../../../src/theme';

export default function ProvenanceScreen() {
  const router = useRouter();
  const { factId } = useLocalSearchParams<{ factId: string }>();

  if (!factId) {
    return (
      <Screen padded>
        <View style={styles.centeredState} accessibilityRole="alert">
          <Text variant="body" color="danger" style={styles.centered}>
            No fact ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  return (
    <Screen padded={false} edges={['top', 'left', 'right']}>
      {/* Screen header */}
      <View style={styles.header}>
        <Button
          title="Back"
          variant="ghost"
          size="sm"
          fullWidth={false}
          onPress={() => router.back()}
          accessibilityLabel="Go back"
          style={styles.backBtn}
        />
        <Text variant="subhead">Provenance</Text>
        {/* Spacer to centre the title */}
        <View style={styles.backBtn} />
      </View>

      <ProvenanceViewer factId={factId} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  backBtn: {
    minWidth: theme.spacing['3xl'],
  },
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
  },
  centered: {
    textAlign: 'center',
  },
});
